// ---------------------------------------------------------------------------
// Cognito, for the console
// ---------------------------------------------------------------------------
// Support staff sign in against this stack's own user pool. The password goes
// from the browser to Cognito over SRP and never touches the support API — so
// the API has no credential to mishandle, and a compromise of it does not
// yield the means to impersonate anybody.
//
// The pool id and app client id below are compiled into the bundle. That is
// correct and not a leak: an app client without a secret is a public
// identifier, exactly like an OAuth client id. What protects the pool is the
// password, the MFA factor and the group membership — none of which are here.
//
// Clinic users do not sign in through this module. They reach support from
// inside their own CliniqVoice account, which already holds their session; the
// API accepts that token directly (server middleware/cognitoAuth.js).
// ---------------------------------------------------------------------------

import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';

const env = import.meta.env;

export const cognitoConfig = {
  userPoolId: env.VITE_SUPPORT_USER_POOL_ID,
  userPoolClientId: env.VITE_SUPPORT_CLIENT_ID,
  region: env.VITE_AWS_REGION,
};

/**
 * Whether sign-in is possible at all.
 *
 * A build with these unset produces a console nobody can enter. Saying so on
 * the sign-in screen is worth the four lines — the alternative is an
 * authentication form that fails with a stack trace from inside Amplify.
 */
export const isCognitoConfigured = Boolean(
  cognitoConfig.userPoolId && cognitoConfig.userPoolClientId,
);

if (isCognitoConfigured) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: cognitoConfig.userPoolId,
        userPoolClientId: cognitoConfig.userPoolClientId,
        loginWith: { username: false, email: true },
      },
    },
  });
}

/**
 * The ID token for the current session, or null when there is none.
 *
 * The ID token rather than the access token, because the API authenticates an
 * identity: it needs the email and the group membership, and Cognito puts
 * those on the ID token. Amplify refreshes it as needed, so this is called
 * fresh on every request rather than cached anywhere.
 */
export async function idToken() {
  if (!isCognitoConfigured) return null;
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    // No session, or a refresh token that has expired or been revoked — which
    // is what a role change or a deactivation looks like from here. Either way
    // the caller's next request is anonymous and the app returns to sign-in.
    return null;
  }
}
